import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { useGetFileProcessingStatusAndErrorFile } from '../../../../api-refactored/non-national-results/useGetFileProcessingStatusAndError';
import { usePostUploadPresignedUrl } from '../../../../api-refactored/non-national-results/usePostUploadPresignedUrl';
import { usePutStartImportResultsFileProcessing } from '../../../../api-refactored/non-national-results/usePutStartImportResultsFileProcessing';
import { usePutUploadToS3 } from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import ImportButton from '../../../../components/buttons/ImportButton';
import { FileProcessingState, FileUploadState } from '../../../../models/File';
import { uploadFile } from '../../../../utils/FileUtils';
import ImportResultsDropzone from './ImportResultsDropzone';
import { ComponentFileProcessingState } from './UploadSection/model';

export interface ImportResultsModalProps {
  modelOpen: boolean;
  onClose: (value?: string) => void;
  handlReloadData: React.Dispatch<React.SetStateAction<number>>;
}

const useStyles = makeStyles()((theme) =>
({
  importResultsDialog: {
    '& .MuiPaper-root.MuiDialog-paper': {
      padding: theme.spacing(3),
      '& .MuiDialogTitle-root': {
        padding: 0,
      },
      '& .MuiDialogContent-root': {
        padding: 0,
      },
      '& .MuiDialogActions-root': {
        padding: 0,
      },
    },
  },
  dropZone: {
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  importResultsStatement: {
    marginBottom: theme.spacing(4),
    color: theme.color.text.textPrimary,
  },
  closeButton: {
    padding: 0,
  },
  instruction: {
    color: theme.color.text.textPrimary,
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  uploadingFileContainer: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(7),
  },
  uploadErrorSection: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      marginRight: theme.spacing(2.25),
    },
  },
  uploadErrorDescription: {
    color: theme.color.text.textPrimary,
    fontWeight: 700,
  },
  errorIcon: {
    color: theme.color.state.stateError,
    fontSize: theme.spacing(5.625),
  },
  loadingSection: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(1),
  },
}));

const ImportResultsModal = ({ modelOpen, onClose, handlReloadData }: ImportResultsModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();

  const [fileProcessingStatus, setFileProcessingStatus] = useState<
    ComponentFileProcessingState | undefined
  >();

  const getUploadPresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res) => {
      uploadFile(res, uploadedFiles, uploadMutation.mutate);
    },
  });

  const fileUuid = getUploadPresignedUrlMutation.data?.key || '';

  const startFileProcessingMutation = usePutStartImportResultsFileProcessing({
    onSuccess: () => {
      setFileProcessingStatus(ComponentFileProcessingState.PROCESSING);
    },
  });

  const getDocumentUploadStatus = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && startFileProcessingMutation.isIdle,
    refetchInterval: 5000,
  });

  const fileStatus = getDocumentUploadStatus.data?.fileStatus;

  const [isS3Success, setIsS3Success] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const [errorFileDownloadUrl, setErrorFileDownloadUrl] = useState<string>();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const startFileProcessingMutationFn = startFileProcessingMutation.mutate;

  useEffect(() => {
    if (fileStatus === FileUploadState.SUCCESS && fileUuid) {
      startFileProcessingMutationFn({ fileUuid });
    } else if (fileStatus === FileUploadState.FAILED || fileStatus === FileUploadState.TIMEOUT) {
      setHasError(true);
      setFileProcessingStatus(undefined);
      setUploadedFiles([]);
    }
  }, [fileStatus, fileUuid, startFileProcessingMutationFn, setHasError]);

  const uploadMutation = usePutUploadToS3({
    onSuccess: () => {
      setIsS3Success(true);
    },
  });

  const uploadFileClickHandler = () => {
    setFileProcessingStatus(ComponentFileProcessingState.IDLE);
    getUploadPresignedUrlMutation.mutate({ uuid: '', fileName: uploadedFiles[0].name });
  };

  const resetApi = () => {
    startFileProcessingMutation.reset();
    getUploadPresignedUrlMutation.reset();
    getDocumentUploadStatus.refetch();
  };
  const resetStates = () => {
    setIsS3Success(false);
    setHasError(false);
    setUploadedFiles([]);
    setErrorFileDownloadUrl(undefined);
    setFileProcessingStatus(undefined);
  };

  const reset = () => {
    resetApi();
    resetStates();
  };

  const loading =
    fileProcessingStatus === ComponentFileProcessingState.IDLE ||
    fileProcessingStatus === ComponentFileProcessingState.PROCESSING ||
    [FileUploadState.GENERATING, FileUploadState.UPLOADING].includes(fileStatus as any);

  const needGetProcessingStatus =
    !!fileUuid &&
    isS3Success &&
    fileStatus === FileUploadState.SUCCESS &&
    fileProcessingStatus !== ComponentFileProcessingState.ERROR;

  useGetFileProcessingStatusAndErrorFile({
    uuid: fileUuid,
    enabled: needGetProcessingStatus,
    refetchInterval: 5000,
    onSuccess: (res) => {
      if (res.status === FileProcessingState.SUCCESS) {
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'success',
          targetData: t('exam->>NonNationalExam.UploadResultSuccessMessage', {
            resultsNumber: res.numberOfRecords,
          }),
        });
        setFileProcessingStatus(ComponentFileProcessingState.SUCCESS);
        handlReloadData((prev) => prev + 1);
        handleClose();
      }

      if (res.status === FileProcessingState.ERROR) {
        setFileProcessingStatus(ComponentFileProcessingState.ERROR);
        setErrorFileDownloadUrl(res.presignUrl);
        setIsS3Success(false);
        setUploadedFiles([]);
        resetApi();
      }
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog className={classes.importResultsDialog} open={modelOpen} maxWidth="md">
      <>
        <Box className={classes.titleContainer}>
          <DialogTitle>
            <Typography variant="h4">{t('exam->>NonNationalExam.ImportResults')}</Typography>
          </DialogTitle>
        </Box>

        <DialogContent>
          <ImportResultsDropzone
            classes={classes}
            translation={t}
            stateHandler={{
              setFileProcessingStatusHandler: {
                fileProcessingStatus,
                setFileProcessingStatus,
              },
              setUploadedFilesHandler: {
                uploadedFiles,
                setUploadedFiles,
              },
            }}
            fileStatus={getDocumentUploadStatus.data?.fileStatus}
            errorFileDownloadUrl={errorFileDownloadUrl}
            loading={loading}
            files={uploadedFiles}
          />
          {hasError && (
            <Typography className={classes.errorText}>
              {t('exam->>NonNationalExam.UploadResultErrorInstruction')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button
              onClick={handleClose}
              variant="text"
              id="cancel-button"
              className={classes.cancelButton}
              disabled={loading}
            >
              {t('common->>Cancel')}
            </Button>
            <ImportButton
              disabled={loading || uploadedFiles.length === 0}
              label="Import File"
              importHandler={uploadFileClickHandler}
            />
          </Box>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default ImportResultsModal;
